import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getOrganizationInvitationEmails,
  getOrganizationInvitations,
  type OrganizationInvitationEmailItem,
  type OrganizationInvitationItem,
} from "@/services/organizationsApi";

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Issue d'un envoi. `sent` est volontairement NEUTRE et non vert : le relais a
 * accepté le message, ce qui ne dit rien de sa réception. Un vert laisserait
 * croire à une livraison que rien ici ne constate.
 */
const OUTCOME_STYLES: Record<string, string> = {
  sent: "bg-gray-100 text-gray-800",
  skipped: "bg-gray-100 text-gray-600",
  failed: "bg-red-100 text-red-800",
  // `failed` split in back/src/core/mail.py: without these, the three new
  // failure values fell back to the neutral chip of `sent`.
  auth_failed: "bg-red-100 text-red-800",
  network_failed: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800",
  not_configured: "bg-red-100 text-red-800",
  credentials_missing: "bg-red-100 text-red-800",
};

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-sky-100 text-sky-800",
  accepted: "bg-emerald-100 text-emerald-800",
  revoked: "bg-gray-100 text-gray-600",
  expired: "bg-amber-100 text-amber-800",
};

function Chip({ value, styles }: { value: string; styles: Record<string, string> }) {
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        styles[value] ?? "bg-gray-100 text-gray-800"
      }`}
    >
      {value}
    </span>
  );
}

/**
 * Invitations d'une organisation, vues du support.
 *
 * Le support traitait « mon collègue n'a jamais reçu son invitation » sans
 * aucune donnée : `status`, `last_email_ok` et `last_email_error` n'existaient
 * que dans l'API client, réservée aux OrgAdmin de l'organisation concernée.
 * Le back-office ne mappait même pas les deux tables.
 */
export default function OrganizationInvitationsSection({
  organizationId,
}: {
  organizationId: number | string;
}) {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<OrganizationInvitationItem[]>([]);
  const [emails, setEmails] = useState<OrganizationInvitationEmailItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inv, log] = await Promise.all([
        getOrganizationInvitations(navigate, organizationId),
        getOrganizationInvitationEmails(navigate, organizationId),
      ]);
      setInvitations(inv);
      setEmails(log);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [navigate, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="border rounded p-4 bg-white">
      <h3 className="text-md font-semibold mb-1">Invitations ({invitations.length})</h3>
      <p className="text-xs text-gray-500 mb-3">
        « Remis au relais » n'est pas « reçu » : un rejet ultérieur (filtre
        anti-spam, boîte pleine) reste invisible ici.
      </p>

      {error && <div className="mb-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="text-sm text-gray-500">Chargement…</div>}

      <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-left">Dernier envoi</th>
              <th className="px-3 py-2 text-left">Tentatives</th>
              <th className="px-3 py-2 text-left">Issue</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="px-3 py-2 break-all">{i.email}</td>
                <td className="px-3 py-2">
                  <Chip value={i.status} styles={STATUS_STYLES} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDate(i.last_sent_at)}
                </td>
                <td className="px-3 py-2">{i.send_attempts}</td>
                <td className="px-3 py-2">
                  {i.last_email_ok === false ? (
                    <span className="text-red-700">
                      {i.last_email_error ?? "échec"}
                    </span>
                  ) : i.last_email_ok === true ? (
                    "remis au relais"
                  ) : (
                    "jamais tenté"
                  )}
                </td>
              </tr>
            ))}
            {!loading && invitations.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-center text-gray-500" colSpan={5}>
                  Aucune invitation
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h4 className="text-sm font-semibold mt-6 mb-1">
        Journal des envois ({emails.length})
      </h4>
      <p className="text-xs text-gray-500 mb-2">
        Une ligne par tentative. Survit au retrait du membre — c'est ce qui
        permet de répondre après coup à « qu'a-t-on envoyé à cette adresse ? ».
      </p>
      <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Destinataire</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Issue</th>
              <th className="px-3 py-2 text-left">Expéditeur</th>
            </tr>
          </thead>
          <tbody>
            {emails.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDate(e.created_at)}
                </td>
                <td className="px-3 py-2 break-all">{e.email}</td>
                <td className="px-3 py-2">{e.kind}</td>
                <td className="px-3 py-2">
                  <Chip value={e.outcome} styles={OUTCOME_STYLES} />
                  {e.error && (
                    <div className="mt-1 text-xs text-gray-500">{e.error}</div>
                  )}
                </td>
                <td className="px-3 py-2 break-all">{e.sender_email ?? "-"}</td>
              </tr>
            ))}
            {!loading && emails.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-center text-gray-500" colSpan={5}>
                  Aucun envoi enregistré
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
